/**
 * Navigation model — single source of truth for the sidebar, Ctrl+K quick
 * search, the mobile bottom nav and the tabbed-page strips.
 *
 * Structure: groups → items → (optional) tabs. Visibility is capability driven:
 *   - a TAB appears only if the user can see it (engineModule/action, hrGrant, roles);
 *   - an ITEM appears only if at least one of its tabs is visible (or itself, if plain);
 *   - a GROUP appears only if at least one item is visible.
 * Permissions are not changed here — the menu only reflects them.
 *
 * URLs are intentionally kept at their existing locations so bookmarks and the
 * links inside emails keep working; General Ledger is the one screen whose tabs
 * get their own new URLs (and /general-ledger redirects to the first tab).
 */
import {
  LayoutDashboard, Box, Users2, Building, CalendarRange, Radio, Landmark, Cpu, Lock,
  Package, Warehouse, MapPin, ClipboardList, ArrowLeftRight, FileText, Car, Gift,
  Users, ShieldCheck, ClipboardCheck, Building2, Briefcase, ScrollText, FilePlus2,
  Megaphone, BookOpen, Contact, Mail, SlidersHorizontal, ReceiptText,
  Truck, BarChart3, TrendingUp, UserCircle, Upload, History, Activity, Scale, FileBadge,
} from 'lucide-react';

export const DASHBOARD = { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, href: '/' };

export const NAV = [
  {
    id: 'assethub', label: 'AssetHub', icon: Box,
    items: [
      { id: 'assets', name: 'Assets', icon: Package, tabs: [
        { name: 'Catalog', href: '/items', engineModule: 'assets' },
        { name: 'Uniform Inventory', href: '/inventory', engineModule: 'assets' },
        { name: 'Warehouses', href: '/locations', engineModule: 'employees' },
      ] },
      { id: 'requisitions', name: 'Requisitions', icon: ClipboardList, tabs: [
        { name: 'Uniform', href: '/requests', engineModule: 'requests' },
        { name: 'IT', href: '/asset-checkouts', engineModule: 'requests' },
      ] },
      { id: 'purchase-orders', name: 'Purchase Orders', icon: FileText, href: '/purchase-orders', engineModule: 'purchase_orders' },
      { id: 'fleet', name: 'Fleet', icon: Car, href: '/fleet', engineModule: 'fleet' },
      { id: 'marketing', name: 'Marketing', icon: Gift, href: '/marketing-swag', engineModule: 'marketing' },
    ],
  },
  {
    id: 'people', label: 'People & Places', icon: Users2,
    items: [
      { id: 'employees', name: 'Employees', icon: Users, href: '/employees', engineModule: 'employees' },
      { id: 'supervisory-groups', name: 'Supervisory Groups', icon: ShieldCheck, href: '/supervisory-groups', engineModule: 'employees', roles: ['admin', 'manager'] },
      { id: 'office-locations', name: 'Office Locations', icon: Building, href: '/places/offices' },
      { id: 'evaluations', name: 'Evaluations', icon: ClipboardCheck, tabs: [
        { name: 'Evaluations', href: '/evaluations', engineModule: 'evaluations' },
        { name: 'Completion', href: '/evaluation-completion', engineModule: 'evaluations' },
      ] },
    ],
  },
  {
    id: 'clients-jobs', label: 'Clients & Jobs', icon: Building,
    items: [
      { id: 'my-accounts', name: 'My Accounts', icon: Building, href: '/my-accounts', engineModule: 'evaluations', roles: ['admin', 'hr', 'manager', 'supervisor'] },
      { id: 'clients', name: 'Clients', icon: Building2, href: '/clients', engineModule: 'clients' },
      { id: 'sites', name: 'Sites', icon: MapPin, href: '/sites', engineModule: 'clients' },
      { id: 'jobs', name: 'Jobs', icon: Briefcase, href: '/jobs', engineModule: 'jobs' },
      { id: 'work-orders', name: 'Work Orders', icon: ClipboardList, href: '/work-orders', engineModule: 'jobs' },
    ],
  },
  {
    id: 'scheduling', label: 'Scheduling', icon: CalendarRange,
    items: [
      { id: 'schedule', name: 'Schedule', icon: CalendarRange, href: '/scheduling', engineModule: 'scheduling' },
      { id: 'timesheets', name: 'Timesheets', icon: ClipboardCheck, href: '/timesheets', engineModule: 'scheduling' },
    ],
  },
  {
    id: 'gsoc', label: 'GSOC', icon: Radio,
    items: [
      { id: 'gsoc-log', name: 'Activity Log', icon: ScrollText, href: '/gsoc', engineModule: 'gsoc' },
      { id: 'gsoc-new', name: 'New Entry', icon: FilePlus2, href: '/gsoc/new', engineModule: 'gsoc', action: 'add' },
      { id: 'gsoc-passon', name: 'Pass-On', icon: Megaphone, tabs: [
        { name: 'Pass-On Log', href: '/gsoc/pass-on', engineModule: 'gsoc' },
        { name: 'Acknowledgments', href: '/gsoc/pass-on/audit', engineModule: 'gsoc_passon' },
      ] },
      { id: 'gsoc-sop', name: 'SOP', icon: BookOpen, href: '/gsoc/sop', engineModule: 'gsoc' },
      { id: 'gsoc-contacts', name: 'Contacts', icon: Contact, href: '/gsoc/contacts', engineModule: 'gsoc' },
      { id: 'gsoc-reports', name: 'Reports', icon: Mail, href: '/gsoc/reports', engineModule: 'gsoc_reports' },
      { id: 'gsoc-settings', name: 'Settings', icon: SlidersHorizontal, tabs: [
        { name: 'Configuration', href: '/gsoc/config', engineModule: 'gsoc_config' },
        { name: 'Log Integrity', href: '/gsoc/integrity', engineModule: 'gsoc_integrity' },
      ] },
    ],
  },
  {
    id: 'finance', label: 'Finance', icon: Landmark,
    items: [
      { id: 'billing-setup', name: 'Billing Setup', icon: ReceiptText, tabs: [
        { name: 'Billing Items', href: '/billing-items', engineModule: 'admin' },
        { name: 'Hours Types', href: '/admin/hours-types', engineModule: 'admin' },
      ] },
      { id: 'vendors', name: 'Vendors', icon: Truck, href: '/vendors', engineModule: 'purchase_orders' },
      { id: 'general-ledger', name: 'General Ledger', icon: BookOpen, tabs: [
        { name: 'Accounts', href: '/general-ledger/accounts', engineModule: 'ledger' },
        { name: 'Trial Balance', href: '/general-ledger/trial-balance', engineModule: 'ledger' },
        { name: 'Journal Entries', href: '/general-ledger/journal-entries', engineModule: 'ledger' },
        { name: 'Chart Import', href: '/general-ledger/chart-import', engineModule: 'ledger' },
        { name: 'Opening Balances', href: '/general-ledger/opening-balances', engineModule: 'ledger' },
      ] },
      // Payroll Review is a Payroll view: the pay-rate queue is gated on the
      // `payroll` HR grant and the self-approved report on scheduling:approve.
      // Finance carries the payroll grant (migration) and admins hold everything,
      // so the menu shows this group to exactly Payroll grant holders, Finance
      // and admins — a manager (who has scheduling:approve but no payroll grant)
      // sees no Finance group.
      { id: 'payroll-review', name: 'Payroll Review', icon: ClipboardCheck, hrGrant: 'payroll', grantRoles: ['finance'], tabs: [
        { name: 'Pay-Rate Review', href: '/scheduling/pay-rate-review', hrGrant: 'payroll', grantRoles: ['finance'] },
        { name: 'Self-Approved Weeks', href: '/scheduling/self-approved', hrGrant: 'payroll', grantRoles: ['finance'] },
      ] },
      // Payroll Parallel Test (v4.35.0, PREVIEW). Gated on its own
      // `payroll_import` module because the Scheduling Activity Report carries
      // pay rates and dollars per row — only Payroll/Finance and anyone granted
      // payroll:import see it. Hours & overtime compared side by side.
      { id: 'payroll-sar', name: 'Payroll Parallel Test', icon: Scale, tabs: [
        { name: 'SAR Import', href: '/payroll-sar/import', engineModule: 'payroll_import' },
        { name: 'Setup', href: '/payroll-sar/setup', engineModule: 'payroll_import' },
        { name: 'Overtime Parity', href: '/payroll-sar/overtime', engineModule: 'payroll_import' },
        { name: 'Hours Comparison', href: '/payroll-sar/hours', engineModule: 'payroll_import' },
        { name: 'Gross Pay Parity', href: '/payroll-sar/gross-pay', engineModule: 'payroll_import' },
        { name: 'Net Pay', href: '/payroll-net-pay', engineModule: 'payroll_import' },
      ] },
    ],
  },
  {
    id: 'compliance', label: 'Compliance', icon: ShieldCheck,
    items: [
      { id: 'company-licenses', name: 'Company Licenses', icon: FileBadge, href: '/compliance/licenses', engineModule: 'compliance_licenses' },
    ],
  },
  {
    id: 'intelligence', label: 'Intelligence', icon: Cpu,
    items: [
      { id: 'reports', name: 'Reports', icon: BarChart3, href: '/reports', engineModule: 'reports' },
      { id: 'forecasting', name: 'Forecasting', icon: TrendingUp, href: '/forecasting', engineModule: 'reports' },
      { id: 'announcements', name: 'Announcements', icon: Megaphone, href: '/announcements' },
      { id: 'help', name: 'Help', icon: BookOpen, href: '/help' },
    ],
  },
  {
    id: 'admin', label: 'Administration', icon: Lock,
    items: [
      { id: 'users-permissions', name: 'Users & Permissions', icon: UserCircle, tabs: [
        { name: 'Users', href: '/users', engineModule: 'users' },
        { name: 'Permissions', href: '/permissions', engineModule: 'admin' },
      ] },
      { id: 'admin-settings', name: 'Settings', icon: SlidersHorizontal, tabs: [
        { name: 'Evaluation Settings', href: '/admin/evaluation-settings', engineModule: 'admin' },
        { name: 'Alert Recipients', href: '/admin/alert-recipients', engineModule: 'admin' },
      ] },
      { id: 'admin-system', name: 'System', icon: Activity, tabs: [
        { name: 'System Status', href: '/admin/system-status', engineModule: 'admin' },
        { name: 'Diagnostics', href: '/admin/diagnostics', engineModule: 'admin' },
        { name: 'Email Delivery', href: '/admin/email-delivery', engineModule: 'admin' },
      ] },
      { id: 'admin-data', name: 'Data & Audit', icon: History, tabs: [
        { name: 'Import Data', href: '/import', engineModule: 'admin' },
        { name: 'Audit Logs', href: '/audit-logs', engineModule: 'admin' },
      ] },
    ],
  },
];

export const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// ── Visibility ──────────────────────────────────────────────────────────────
// ctx = { role, perms } where perms is the useEffectivePermissions() result.
export function canSee(node, ctx) {
  const { role, perms } = ctx;
  if (role === 'admin') return true;
  if (node.roles && !node.roles.includes(role)) return false;
  if (node.hrGrant) {
    const byGrant = perms?.hasGrant?.(node.hrGrant);
    const byRole = node.grantRoles ? node.grantRoles.includes(role) : false;
    return Boolean(byGrant || byRole);
  }
  if (node.engineModule) {
    if (!perms?.loaded) return false;
    return node.action ? perms.can(node.engineModule, node.action) : perms.canModule(node.engineModule);
  }
  return true;
}

const LAST_TAB_KEY = (itemId) => `nav.lastTab.${itemId}`;

export function rememberTab(itemId, href) {
  try { localStorage.setItem(LAST_TAB_KEY(itemId), href); } catch {/* no-op */}
}

function pickItemHref(item, visibleTabs) {
  let last = null;
  try { last = localStorage.getItem(LAST_TAB_KEY(item.id)); } catch {/* no-op */}
  if (last && visibleTabs.some((t) => t.href === last)) return last;
  return visibleTabs[0].href;
}

export function resolveItem(item, ctx) {
  if (!canSee(item, ctx)) return null;
  if (item.tabs) {
    const tabs = item.tabs.filter((t) => canSee(t, ctx));
    if (!tabs.length) return null;
    return { ...item, tabs, href: pickItemHref(item, tabs), isTabbed: true };
  }
  return { ...item, href: item.href, isTabbed: false };
}

export function resolveGroups(ctx) {
  return NAV
    .map((g) => ({ ...g, items: g.items.map((it) => resolveItem(it, ctx)).filter(Boolean) }))
    .filter((g) => g.items.length > 0);
}

export function findItem(itemId) {
  for (const g of NAV) {
    const it = g.items.find((i) => i.id === itemId);
    if (it) return { group: g, item: it };
  }
  return null;
}

// Every href an item can land on: all tab hrefs for a tabbed item, else its own.
export function hrefsForItem(item) {
  if (item.tabs && item.tabs.length) return item.tabs.map((t) => t.href).filter(Boolean);
  return item.href ? [item.href] : [];
}

// How strongly `pathname` matches `href`: exact or a true path-segment prefix.
// Returns the href length as the score (longer = more specific) or -1 for no
// match. `/` only matches itself.
function hrefMatchScore(href, pathname) {
  if (!href) return -1;
  if (href === '/') return pathname === '/' ? 1 : -1;
  if (pathname === href || pathname.startsWith(href + '/')) return href.length;
  return -1;
}

// The single href that OWNS the current page: the LONGEST nav href the path
// matches. This stops a short prefix (e.g. Scheduling's `/scheduling`) from
// hijacking a deeper page that belongs to another item
// (`/scheduling/pay-rate-review` → Finance → Payroll Review), and stops a group
// landing page (`/gsoc`) from co-highlighting its own sub-pages (`/gsoc/new`).
// `groups` is the visibility-resolved list from resolveGroups().
export function activeHref(groups, pathname) {
  let best = null;
  let bestScore = 0;
  for (const g of groups) {
    for (const it of g.items) {
      for (const h of hrefsForItem(it)) {
        const score = hrefMatchScore(h, pathname);
        if (score > bestScore) { bestScore = score; best = h; }
      }
    }
  }
  return best;
}

// Flat list of every screen the user can open, for Ctrl+K quick search.
export function flatScreens(ctx) {
  const out = [{ name: 'Dashboard', href: '/', group: 'Dashboard', icon: DASHBOARD.icon }];
  for (const g of resolveGroups(ctx)) {
    for (const it of g.items) {
      if (it.isTabbed) {
        for (const t of it.tabs) out.push({ name: `${it.name} · ${t.name}`, href: t.href, group: g.label, icon: it.icon });
      } else {
        out.push({ name: it.name, href: it.href, group: g.label, icon: it.icon });
      }
    }
  }
  return out;
}
